import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { getUserFullName } from '../helpers/user';
import useRescuerDashboardActions from '../hooks/useRescuerDashboardActions';
import { getPercentage } from '../helpers/math';
import routes from '../routes';
import { getLabelFromType } from '../helpers/siteGoals';
import OverlayLoader from './OverlayLoader';
import { setSiteGoalsPrompt } from '../actions/uiPersisted';

const ClaimRescueBasedOnSiteGoalDialog = ({ rescuer }) => {
  const [dialogOpen, setDialogOpen] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();
  const siteGoalsEntities = useSelector((state) => state.entities.siteGoals.byRescuerId);
  const showSiteGoalsPrompt = useSelector((state) => state.ui.persisted.siteGoalsPrompt?.[rescuer?.id]);
  const { fetchGoals } = useRescuerDashboardActions();
  const user = getUserFullName(rescuer);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
  const textSx = {
    marginBottom: 2,
  };

  useEffect(() => {
    fetchGoals(rescuer.id, false);
  }, [user]);

  const siteGoalsInProgress = Object.values(siteGoalsEntities?.[rescuer?.id] || {})
    .filter((siteGoal) => siteGoal.goal > siteGoal.value)
    .sort((siteGoal1, siteGoal2) => siteGoal1.site_id - siteGoal2.site_id);

  const handleClaimRescue = () => {
    setDialogOpen(false);
    dispatch(setSiteGoalsPrompt(rescuer.id, { user_id: rescuer.id, viewDate: moment() }));

    if (isMobileView) {
      return history.push(generatePath(routes.mobileDashboardClaimRescue));
    }

    return history.push(generatePath(routes.dashboardRescuer, { tab: 'schedule' }));
  };

  const handleClose = () => {
    setDialogOpen(false);
    dispatch(setSiteGoalsPrompt(rescuer.id, { user_id: rescuer.id, viewDate: moment() }));
  };

  const savedDateIsSameAsCurrentMonth = showSiteGoalsPrompt?.viewDate
    ? moment(showSiteGoalsPrompt.viewDate).isSame(moment(), 'month')
    : true;
  // Display dialog during the last week of the month
  const displayStartDate = moment().endOf('month').subtract(7, 'days');
  const displayEndDate = moment().endOf('month');
  const currentDateIsBetweenLastWeek = moment().isBetween(displayStartDate, displayEndDate, 'days', '[]');

  if (
    (siteGoalsInProgress && siteGoalsInProgress.length < 1) ||
    (showSiteGoalsPrompt?.user_id === rescuer.id && savedDateIsSameAsCurrentMonth) ||
    !currentDateIsBetweenLastWeek
  )
    return null;

  return (
    <Dialog
      open={dialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      onClose={handleClose}
    >
      <OverlayLoader isLoading={siteGoalsEntities.inflight}>
        <DialogTitle sx={containerSx}>
          Hello, {user}!
          <CloseIcon
            sx={{ cursor: 'pointer', position: 'absolute', top: theme.spacing(1), right: theme.spacing(1) }}
            onClick={handleClose}
          />
        </DialogTitle>
        <DialogContent sx={containerSx}>
          {siteGoalsInProgress.map((siteGoal) => (
            <Typography key={siteGoal.id} sx={textSx}>
              {siteGoal.name} is {getPercentage(siteGoal.value, siteGoal.goal)}% to meeting their goal of{' '}
              {getLabelFromType(siteGoal.type)}.
            </Typography>
          ))}
          <Typography sx={textSx}>Can you claim a rescue to help?</Typography>
        </DialogContent>
        <DialogActions sx={containerSx}>
          <Button variant="contained" color="primary" onClick={handleClaimRescue}>
            Claim Rescue
          </Button>
        </DialogActions>
      </OverlayLoader>
    </Dialog>
  );
};

export default ClaimRescueBasedOnSiteGoalDialog;
